/**
 * OAuth Callback Page
 * Handles OAuth redirect — server sets httpOnly cookies, client reads status from query param
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { logger } from '../utils/logger';
import { isSafeReturnPath } from './oauthCallbackUtils';

export const OAuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing OAuth callback...');
  const hasRun = React.useRef(false);
  const redirectTimerRef = React.useRef<number | null>(null);

  useEffect(() => {
    // Prevent double token extraction in React StrictMode,
    // but always ensure the redirect timer is scheduled.
    if (!hasRun.current) {
      hasRun.current = true;
      handleCallback();
    }
    // No cleanup — the redirect must survive StrictMode's unmount/remount cycle.
    // The timer fires once (navigates away) so there's nothing to leak.
    // Mount-only: callback reads URL params once; handleCallback closes over
    // navigate/searchParams and re-running on their identity churn would re-trigger redirects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = () => {
    try {
      // Server sets httpOnly cookies and redirects with ?status=success
      const callbackStatus = searchParams.get('status');

      if (callbackStatus === 'success') {
        setStatus('success');
        setMessage('Authentication successful! Redirecting...');

        // Clear any lingering demo mode from a previous demo session
        sessionStorage.removeItem('demoMode');
        sessionStorage.removeItem('flowType');

        const savedReturnUrl = sessionStorage.getItem('auth_return_url');
        sessionStorage.removeItem('auth_return_url');
        const returnUrl =
          savedReturnUrl && isSafeReturnPath(savedReturnUrl) ? savedReturnUrl : '/dashboard';

        // Redirect to the original protected page — cookies are already set by the server.
        // Use window.location.href to force a full page reload so JWTAuthContext
        // re-initializes and picks up the new session from /auth/me
        redirectTimerRef.current = window.setTimeout(() => {
          window.location.href = returnUrl;
        }, 1000);
      } else {
        throw new Error(searchParams.get('error') || 'Authentication failed');
      }
    } catch (error) {
      logger.error('[OAuth Callback] Authentication failed', { error });
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Failed to complete authentication');

      // Redirect to login after 3 seconds on error
      redirectTimerRef.current = window.setTimeout(() => {
        navigate('/signin', { replace: true });
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-500 dark:border-teal-400 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Processing...
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-green-500 dark:text-green-400 text-6xl mb-4">✓</div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Success!</h2>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-red-500 dark:text-red-400 text-6xl mb-4">✗</div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Authentication Failed
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
              <button
                onClick={() => navigate('/signin')}
                className="mt-4 px-6 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors"
              >
                Return to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
